import { FC, useMemo } from "react";
import moment from "moment";
import { Icon } from "@/components";
import { Badge, IconButton, Table, type TableColumn } from "@/ui";
import { useStoreContext } from "context";
import { convertTZ } from "@/utils";
import type { AlgoliaOrder } from "@/types";
import badgeStyles from "./orderBadges.module.scss";
import { getOrderStatusTone, getOrderTypeTone } from "./orderVisualTokens";
import styles from "./OrderTable.module.scss";

export type OrderTableProps = {
    orders: AlgoliaOrder[];
    handleModalToggle: (order: AlgoliaOrder) => void;
    handleDetailOpen: (id: string) => void;
};

/**
 * Orders list table — Treez DS `Table` wired to Algolia order rows.
 */
export const OrderTable: FC<OrderTableProps> = ({
    orders,
    handleModalToggle,
    handleDetailOpen,
}) => {
    const { store } = useStoreContext();

    const columns = useMemo<TableColumn<AlgoliaOrder>[]>(
        () => [
            {
                id: "actions",
                header: "Actions",
                width: 56,
                minWidth: 56,
                align: "center",
                renderCell: row => (
                    <span onClick={event => event.stopPropagation()}>
                        <IconButton
                            aria-label={`Edit order ${row.order_number}`}
                            onClick={() => handleDetailOpen(row.objectID)}
                            size="sm"
                            type="button"
                            variant="tertiary"
                        >
                            <Icon icon="pencil" />
                        </IconButton>
                    </span>
                ),
            },
            {
                id: "order_number",
                header: "Order Number",
                width: "6%",
                minWidth: 96,
                renderCell: row => (
                    <p className={styles.primary}>#{row.order_number}</p>
                ),
            },
            {
                id: "created_at",
                header: "Created At",
                width: "15%",
                minWidth: 140,
                renderCell: row => {
                    const localUTCDate = row.created_at
                        ? convertTZ(row.created_at, store?.timezone)
                        : "";
                    const date = localUTCDate
                        ? moment(localUTCDate).format("MMMM Do YYYY")
                        : "";
                    const time = localUTCDate
                        ? moment(localUTCDate).format("HH:mm:ss")
                        : "";
                    return (
                        <div className={styles.stack}>
                            <p className={styles.primary}>{date}</p>
                            <p className={styles.secondary}>{time}</p>
                        </div>
                    );
                },
            },
            {
                id: "type",
                header: "Type",
                width: "12%",
                minWidth: 100,
                renderCell: row => (
                    <Badge
                        className={badgeStyles.typeBadge}
                        data-tone={getOrderTypeTone(row.type)}
                        variant="neutral"
                    >
                        {row.type}
                    </Badge>
                ),
            },
            {
                id: "status",
                header: "Status",
                width: "12%",
                minWidth: 120,
                renderCell: row => (
                    <Badge
                        className={badgeStyles.statusBadge}
                        data-tone={getOrderStatusTone(row.status)}
                        variant="neutral"
                    >
                        {row.status.replaceAll("_", " ")}
                    </Badge>
                ),
            },
            {
                id: "total_price",
                header: "Price",
                width: "12%",
                minWidth: 88,
                renderCell: row => (
                    <p className={styles.primary}>{row.total_price || "$0"}</p>
                ),
            },
            {
                id: "customer",
                header: "Customer",
                width: "30%",
                minWidth: 160,
                renderCell: row => (
                    <div className={styles.stack}>
                        <p className={styles.primary}>{row.customer}</p>
                        <p className={styles.secondary}>{row.address}</p>
                    </div>
                ),
            },
        ],
        [handleDetailOpen, store?.timezone],
    );

    return (
        <Table
            columns={columns}
            emptyMessage="No results"
            getRowId={row => row.objectID}
            onRowClick={handleModalToggle}
            rows={orders}
        />
    );
};

export default OrderTable;
